import { create } from 'zustand'
import { DEFAULT_P5_VERSION } from '../config/p5versions'

// Module-scoped ref for console batch timeout (avoids Zustand state memory leak)
let consoleBatchTimeoutRef = null

export const defaultSketchCode = `// ============================================================
// Welcome to p5js AI - Mobile-Friendly Creative Coding
// Learn how to Videcode, then create your own! https://p5js.ai
// ============================================================

let robotoFont;
let particles = [];
let step = 0;
let stepTimer = 0;
let stepDuration = 225; // 3.75 seconds per step (+25%)

// Instructions for new users - mobile-first onboarding
let instructions = [
  { title: "Welcome to p5js AI!", desc: "Swipe between panels to explore" },
  { title: "AI Chat", desc: "Ask AI to create or modify your code" },
  { title: "Preview", desc: "See your sketch running live here" },
  { title: "Code", desc: "View and edit the generated code" },
  { title: "Enable Notifications", desc: "Complex sketches take time - get notified!" },
  { title: "Gallery & Tutorials", desc: "Explore examples and learn p5.js" },
  { title: "Why We Built This", desc: "Making creative coding accessible to everyone" },
  { title: "Read the Docs", desc: "Visit /docs for guides, tips, and API reference" },
  { title: "Ready to Create?", desc: "Swipe to AI Chat and type your idea!" },
  { title: "Share Your Ideas!", desc: "Email ideas@p5js.ai or jobs@p5js.ai" }
];

function preload() {
  robotoFont = loadFont('https://unpkg.com/@fontsource/roboto@latest/files/roboto-latin-400-normal.woff');
}

function setup() {
  createCanvas(windowWidth, windowHeight);
  textFont(robotoFont);
  colorMode(HSB, 360, 100, 100, 100);
  noStroke();
  
  // Create floating particles for subtle background animation
  for (let i = 0; i < 25; i++) {
    particles.push({
      x: random(width),
      y: random(height),
      size: random(4, 10),
      speed: random(0.2, 0.8),
      hue: random(360)
    });
  }
}

function draw() {
  // Dark gradient background
  background(240, 25, 10);
  
  // Animate floating particles
  for (let p of particles) {
    p.y -= p.speed;
    if (p.y < -20) {
      p.y = height + 20;
      p.x = random(width);
    }
    p.hue = (p.hue + 0.3) % 360;
    fill(p.hue, 50, 70, 15);
    circle(p.x, p.y, p.size);
  }
  
  // Auto-advance steps
  stepTimer++;
  if (stepTimer >= stepDuration) {
    stepTimer = 0;
    step = (step + 1) % instructions.length;
  }
  
  let current = instructions[step];
  let progress = stepTimer / stepDuration;
  
  // Smooth fade in/out
  let alpha = progress < 0.15 ? map(progress, 0, 0.15, 0, 100) :
              progress > 0.85 ? map(progress, 0.85, 1, 100, 0) : 100;
  
  let centerY = height / 2;
  
  // Animated step number with gentle pulse
  let pulse = 1 + sin(frameCount * 0.08) * 0.08;
  let iconSize = 50 * pulse;
  
  // Draw circular badge with step number
  fill(280, 60, 90, alpha * 0.9);
  circle(width / 2, centerY - 50, iconSize);
  
  // Step number inside circle
  fill(0, 0, 100, alpha);
  textSize(24 * pulse);
  textAlign(CENTER, CENTER);
  text(step + 1, width / 2, centerY - 50);
  
  // Title - responsive size
  textSize(min(26, width / 15));
  fill(280, 70, 100, alpha);
  text(current.title, width / 2, centerY + 15);
  
  // Description - responsive size
  textSize(min(16, width / 24));
  fill(0, 0, 75, alpha * 0.85);
  text(current.desc, width / 2, centerY + 50);
  
  // Progress indicator dots
  let dotSpacing = 14;
  let dotsWidth = (instructions.length - 1) * dotSpacing;
  let dotsX = (width - dotsWidth) / 2;
  
  for (let i = 0; i < instructions.length; i++) {
    let isActive = i === step;
    fill(isActive ? color(280, 70, 100) : color(0, 0, 30));
    circle(dotsX + i * dotSpacing, height - 35, isActive ? 7 : 4);
  }
  
  // Swipe hint
  textSize(11);
  fill(0, 0, 45, 50);
  text("Tap to skip · Swipe panels to navigate", width / 2, height - 60);
}

function mousePressed() {
  step = (step + 1) % instructions.length;
  stepTimer = 0;
}

function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
}`;

export const useStore = create((set, get) => ({
  // User state
  user: null,
  isAuthenticated: false,
  p5Credentials: null,
  
  // Editor state
  code: defaultSketchCode,
  fileName: 'sketch.js',
  currentFile: 'sketch.js',
  files: [
    { 
      name: 'sketch.js', 
      content: defaultSketchCode,
      type: 'js'
    },
    { 
      name: 'index.html', 
      content: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>p5.js Sketch</title>
  <script src="https://cdn.jsdelivr.net/npm/p5@1.11.3/lib/p5.min.js"></script>
  <link rel="stylesheet" href="style.css">
</head>
<body>
  <script src="sketch.js"></script>
</body>
</html>`,
      type: 'html'
    },
    { 
      name: 'style.css', 
      content: `body {
  margin: 0;
  padding: 0;
  overflow: hidden;
}

canvas {
  display: block;
}`,
      type: 'css'
    }
  ],
  
  // Sketch state
  sketchId: null,
  sketchName: `Sketch ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`, // Generate unique name with timestamp
  isSaved: true,
  p5Version: DEFAULT_P5_VERSION, // p5.js library version (from centralized config)
  
  // AI Assistant state
  aiProvider: 'xelsed', // 'openai', 'anthropic', 'kimi', 'gemini', 'xelsed'
  aiModel: 'xelsed-alpha06-flash', // Default: Xelsed Alpha6 Flash
  aiReasoningMode: 'high', // 'minimal' (no thinking), 'low', 'medium', 'high' (advanced thinking)
  aiMessages: [],
  isAiThinking: false,
  showAiPanel: true,
  
  // UI state
  showConsole: true,
  consoleMessages: [],
  consoleMessageIdCounter: 0,
  
  // Editor settings (user-configurable)
  editorSettings: {
    fontSize: 14,
    wordWrap: true,
    minimap: false,
    bracketColors: true,
    indentGuides: true,
    lineHighlight: true,
    smoothScrolling: true,
    mouseWheelZoom: true,
    stickyScroll: false,
  },
  
  // Research state
  participantId: null,
  sessionId: null,
  currentIterationId: null,
  iterationIndex: 0,
  shouldShowSurvey: false,
  currentSketchBefore: null,
  
  // AI Job persistence state (for background completion & recovery)
  activeJobId: null,
  recoveredJob: null, // Stores recovered job data when reconnecting
  
  // Track if user has submitted first prompt (for notification timing)
  hasSubmittedFirstPrompt: false,
  
  // AI change history for undo feature
  aiChangeHistory: [], // [{ fileName, beforeContent, afterContent, timestamp }]
  
  // Include base64 binary data in AI context (for multimodal support)
  includeBase64InContext: false,
  
  // Actions
  setUser: (user) => set({ user, isAuthenticated: !!user }),
  
  setP5Credentials: (credentials) => set({ p5Credentials: credentials }),
  
  setCode: (code) => {
    const { currentFile, files } = get()
    const newFiles = files.map(f => 
      f.name === currentFile ? { ...f, content: code } : f
    )
    set({ 
      code, 
      files: newFiles, 
      isSaved: false 
    })
  },
  
  setCurrentFile: (fileName) => {
    const { files } = get()
    const file = files.find(f => f.name === fileName)
    if (file) {
      set({ 
        currentFile: fileName, 
        code: file.content,
        fileName: file.name
      })
    }
  },
  
  setFiles: (newFiles) => {
    set({ 
      files: newFiles,
      currentFile: newFiles[0]?.name || 'sketch.js',
      code: newFiles[0]?.content || '',
      fileName: newFiles[0]?.name || 'sketch.js'
    })
  },

  addFile: (name, content = '') => {
    const { files } = get()
    
    // Sanitize file name: remove path separators and invalid characters
    const sanitizedName = name
      .replace(/[\/\\]/g, '')  // Remove path separators
      .replace(/\.\./g, '')    // Remove directory traversal
      .replace(/[<>:"|?*\x00-\x1f]/g, '') // Remove invalid file name characters
      .trim()
    
    if (!sanitizedName) return // Don't add empty file names
    
    // Auto-detect file type from extension
    let type = 'other'
    if (sanitizedName.endsWith('.js')) type = 'js'
    else if (sanitizedName.endsWith('.html')) type = 'html'
    else if (sanitizedName.endsWith('.css')) type = 'css'
    else if (sanitizedName.endsWith('.json')) type = 'json'
    else if (sanitizedName.endsWith('.txt') || sanitizedName.endsWith('.md')) type = 'text'
    else if (/\.(png|jpg|jpeg|gif|svg|bmp|ico|webp)$/i.test(sanitizedName)) type = 'image'
    else if (/\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(sanitizedName)) type = 'audio'
    
    const newFile = { name: sanitizedName, content, type }
    set({ 
      files: [...files, newFile],
      currentFile: sanitizedName,
      code: content,
      fileName: sanitizedName
    })
  },
  
  deleteFile: (fileName) => {
    const { files, currentFile } = get()
    if (files.length <= 1) return // Keep at least one file
    
    const newFiles = files.filter(f => f.name !== fileName)
    const nextFile = currentFile === fileName ? newFiles[0] : files.find(f => f.name === currentFile)
    
    set({ 
      files: newFiles, 
      currentFile: nextFile.name,
      code: nextFile.content,
      fileName: nextFile.name
    })
  },

  updateFileContent: (fileName, newContent, switchToFile = true) => {
    const { files } = get()
    const updatedFiles = files.map(f => 
      f.name === fileName ? { ...f, content: newContent } : f
    )
    
    if (switchToFile) {
      set({ 
        files: updatedFiles,
        currentFile: fileName,
        code: newContent,
        fileName: fileName,
        isSaved: false
      })
    } else {
      set({ 
        files: updatedFiles,
        isSaved: false
      })
    }
  },
  
  setSketchInfo: (info) => set({ 
    sketchId: info.id, 
    sketchName: info.name
  }),
  
  setSketchName: (name) => set({ sketchName: name, isSaved: false }),
  
  setSaved: (saved) => set({ isSaved: saved }),
  
  setIncludeBase64InContext: (include) => set({ includeBase64InContext: include }),
  
  addAiMessage: (message) => {
    const { aiMessages } = get()
    set({ aiMessages: [...aiMessages, message] })
    return aiMessages.length // Return the index of the newly added message
  },
  
  updateAiMessage: (index, updates) => {
    const { aiMessages } = get()
    if (index >= 0 && index < aiMessages.length) {
      const newMessages = [...aiMessages]
      newMessages[index] = { ...newMessages[index], ...updates }
      set({ aiMessages: newMessages })
    }
  },
  
  setAiThinking: (thinking) => set({ isAiThinking: thinking }),
  
  setAiProvider: (provider) => {
    // Auto-update model when provider changes
    let model = 'gpt-5-mini' // OpenAI Responses API default
    if (provider === 'anthropic') {
      model = 'claude-sonnet-4-5-20250929'
    } else if (provider === 'kimi') {
      model = 'kimi-k2-turbo-preview'
    } else if (provider === 'gemini') {
      model = 'gemini-3-pro-preview'
    } else if (provider === 'xelsed') {
      model = 'p5flash-alpha02' // Fine-tuned p5.js specialist (xelsed-p5flash-alpha02)
    }
    set({ aiProvider: provider, aiModel: model })
  },
  
  setAiModel: (model) => set({ aiModel: model }),
  
  setAiReasoningMode: (mode) => set({ aiReasoningMode: mode }),
  
  setP5Version: (version) => set({ p5Version: version }),
  
  toggleAiPanel: () => set((state) => ({ showAiPanel: !state.showAiPanel })),
  
  toggleConsole: () => set((state) => ({ showConsole: !state.showConsole })),
  
  // Batch console messages for better performance
  consoleBatch: [],
  
  addConsoleMessage: (message) => {
    const store = get()
    const MAX_MESSAGES = 1000 // Keep only last 1000 messages
    const MAX_BATCH = 100 // Cap batch size to prevent unbounded growth
    
    // Generate stable ID using counter
    const messageId = store.consoleMessageIdCounter + 1
    set({ consoleMessageIdCounter: messageId })
    
    // Cap batch growth - flush early if batch is too large
    if (store.consoleBatch.length >= MAX_BATCH) {
      const { consoleMessages, consoleBatch } = get()
      const newMessages = [...consoleMessages, ...consoleBatch]
      const trimmedMessages = newMessages.slice(-MAX_MESSAGES)
      set({ consoleMessages: trimmedMessages, consoleBatch: [] })
    }
    
    // Add to batch with stable ID
    store.consoleBatch.push({ 
      ...message, 
      id: `console-${messageId}`,
      timestamp: new Date() 
    })
    
    // Clear existing timeout (module-scoped ref, not Zustand state)
    if (consoleBatchTimeoutRef) {
      clearTimeout(consoleBatchTimeoutRef)
    }
    
    // Set new timeout to flush batch
    consoleBatchTimeoutRef = setTimeout(() => {
      const { consoleMessages, consoleBatch } = get()
      const newMessages = [...consoleMessages, ...consoleBatch]
      
      // Keep only last MAX_MESSAGES
      const trimmedMessages = newMessages.slice(-MAX_MESSAGES)
      
      set({ 
        consoleMessages: trimmedMessages,
        consoleBatch: []
      })
      consoleBatchTimeoutRef = null
    }, 10) // Batch for 10ms
  },
  
  clearConsole: () => {
    if (consoleBatchTimeoutRef) {
      clearTimeout(consoleBatchTimeoutRef)
      consoleBatchTimeoutRef = null
    }
    set({ 
      consoleMessages: [], 
      consoleBatch: [],
      consoleMessageIdCounter: 0
    })
  },
  
  // Research actions
  setResearchParticipant: (participantId) => set({ participantId }),
  
  setResearchSession: (sessionId) => set({ sessionId, iterationIndex: 0 }),
  
  triggerSurvey: () => set({ shouldShowSurvey: true }),
  clearSurveyTrigger: () => set({ shouldShowSurvey: false }),
  
  setCurrentIteration: (iterationId, sketchBefore = null) => set({ 
    currentIterationId: iterationId,
    currentSketchBefore: sketchBefore
  }),
  
  incrementIterationIndex: () => set((state) => ({ 
    iterationIndex: state.iterationIndex + 1 
  })),
  
  // AI Job persistence actions
  setActiveJobId: (jobId) => set({ activeJobId: jobId }),
  setRecoveredJob: (job) => set({ recoveredJob: job }),
  clearRecoveredJob: () => set({ recoveredJob: null }),
  
  // AI change history actions (for undo feature)
  saveAIChange: (fileName, beforeContent, afterContent) => set((state) => ({
    aiChangeHistory: [
      ...state.aiChangeHistory.slice(-9), // Keep last 10 changes max
      { fileName, beforeContent, afterContent, timestamp: Date.now() }
    ]
  })),
  
  undoLastAIChange: () => {
    const { aiChangeHistory, files, currentFile } = get()
    if (aiChangeHistory.length === 0) return false
    
    const lastChange = aiChangeHistory[aiChangeHistory.length - 1]
    
    // Restore the file to its previous content
    const newFiles = files.map(f => 
      f.name === lastChange.fileName 
        ? { ...f, content: lastChange.beforeContent }
        : f
    )
    
    // Update state
    set({
      files: newFiles,
      aiChangeHistory: aiChangeHistory.slice(0, -1),
      // If the undone file is current, update code too
      ...(currentFile === lastChange.fileName ? { code: lastChange.beforeContent } : {})
    })
    
    return lastChange.fileName
  },
  
  clearAIChangeHistory: () => set({ aiChangeHistory: [] }),
  
  // Editor settings actions
  updateEditorSettings: (settings) => set((state) => ({
    editorSettings: { ...state.editorSettings, ...settings }
  })),
  
  // Turbo Mode state and actions
  turboMode: true,
  turboRetryCount: 0,
  turboMaxRetries: 3,
  turboOriginalPrompt: null,
  turboChangeHistory: [],
  lastCanvasImage: null,
  
  setTurboMode: (enabled) => set({ turboMode: enabled }),
  
  startTurboSession: (originalPrompt) => set({
    turboRetryCount: 0,
    turboOriginalPrompt: originalPrompt,
    turboChangeHistory: []
  }),
  
  incrementTurboRetry: () => set((state) => ({
    turboRetryCount: state.turboRetryCount + 1
  })),
  
  addTurboAttempt: (error, fixApplied, codeChanges) => set((state) => ({
    turboChangeHistory: [
      ...state.turboChangeHistory,
      { 
        attempt: state.turboRetryCount + 1, 
        error, 
        fixApplied,
        codeChanges,
        timestamp: Date.now()
      }
    ]
  })),
  
  resetTurboSession: () => set({
    turboRetryCount: 0,
    turboOriginalPrompt: null,
    turboChangeHistory: []
  }),
  
  setLastCanvasImage: (dataUrl) => set({ lastCanvasImage: dataUrl }),
  
  // Collaboration / Social Coding state
  collaborationMode: 'solo', // 'solo' or 'group'
  currentRoomId: null,
  currentRoomName: null,
  collaborators: [], // Array of { odId, username, color }
  
  setCollaborationMode: (mode) => set({ collaborationMode: mode }),
  
  joinRoom: (roomId, roomName) => set({
    collaborationMode: 'group',
    currentRoomId: roomId,
    currentRoomName: roomName
  }),
  
  leaveRoom: () => set({
    collaborationMode: 'solo',
    currentRoomId: null,
    currentRoomName: null,
    collaborators: []
  }),
  
  setCollaborators: (collaborators) => set({ collaborators }),
}))

